import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as RevokeIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';

const INTAKE_URL = 'https://excubia.io/api/ingest/alerts';
const PING_URL = 'https://excubia.io/api/ingest/ping';

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const formatDate = (v) => {
  if (!v) return 'Never';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const creatorName = (c) => (c && (c.firstName || c.lastName) ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : (c?.email || '—'));

const curlExample = (prefix) => `curl -X POST ${INTAKE_URL} \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${prefix || 'exc_xxxxxx'}..." \\
  -d '{
    "alerts": [
      {
        "title": "Suspicious PowerShell execution",
        "description": "Encoded command launched from Outlook",
        "severity": "High",
        "category": "Endpoint",
        "source": "EDR",
        "sourceId": "edr-2f9c1a7e",
        "detectionTime": "${new Date().toISOString()}",
        "host": { "hostname": "WS-0142", "ip": "10.20.4.17" },
        "user": "jdoe",
        "indicators": [{ "type": "IP Address", "value": "203.0.113.9" }],
        "tags": ["powershell", "phishing"],
        "raw": { "vendorEventId": "abc123" }
      }
    ]
  }'`;

const CodeBlock = ({ children }) => (
  <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', border: 1, borderColor: 'divider', borderRadius: 1, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre' }}>
    {children}
  </Box>
);

const useCopy = () => {
  const [copied, setCopied] = useState(false);
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard unavailable; user can select the text manually
    }
  };
  return [copied, copy];
};

const ApiKeys = () => {
  const { canManageCompany } = usePermission();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { key, apiKey }
  const [keyCopied, copyKey] = useCopy();
  const [curlCopied, copyCurl] = useCopy();

  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    if (!canManageCompany) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.apiKeys.getAll();
      setKeys(res.data.apiKeys || []);
    } catch (e) {
      setError(serverMessage(e, 'Failed to load API keys'));
    } finally {
      setLoading(false);
    }
  }, [canManageCompany]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await apiService.apiKeys.create({ name: newName.trim() });
      setCreatedKey({ key: res.data.key, apiKey: res.data.apiKey });
      setNewName('');
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Failed to create API key'));
    } finally {
      setCreating(false);
    }
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreatedKey(null);
    setNewName('');
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setError(null);
    try {
      await apiService.apiKeys.revoke(revokeTarget._id);
      setSuccess(`API key "${revokeTarget.name}" revoked`);
      setRevokeTarget(null);
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Failed to revoke API key'));
    } finally {
      setRevoking(false);
    }
  };

  if (!canManageCompany) {
    return <Alert severity="warning">You need the company:manage permission to manage API keys.</Alert>;
  }

  const activeKey = keys.find((k) => !k.revoked);
  const examplePrefix = activeKey ? activeKey.prefix : 'exc_xxxxxx';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">API keys</Typography>
          <Typography variant="body2" color="text.secondary">
            Keys let SIEMs, scripts and other tools send alerts straight into Excubia. The full key is shown only once, at creation.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>Create key</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Prefix</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell align="right">Usage</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <KeyIcon color="disabled" sx={{ fontSize: 36 }} />
                  <Typography color="text.secondary">No API keys yet. Create one to start sending alerts.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((k) => (
                <TableRow key={k._id} hover sx={{ opacity: k.revoked ? 0.6 : 1 }}>
                  <TableCell>{k.name}</TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{k.prefix}…</Typography></TableCell>
                  <TableCell>{(k.scopes || ['ingest']).map((s) => <Chip key={s} label={s} size="small" variant="outlined" sx={{ mr: 0.5 }} />)}</TableCell>
                  <TableCell>{creatorName(k.createdBy)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(k.createdAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(k.lastUsedAt)}</TableCell>
                  <TableCell align="right">{k.usageCount || 0}</TableCell>
                  <TableCell>
                    {k.revoked
                      ? <Tooltip title={`Revoked ${formatDate(k.revokedAt)}`}><Chip label="Revoked" size="small" color="error" /></Tooltip>
                      : <Chip label="Active" size="small" color="success" />}
                  </TableCell>
                  <TableCell align="right">
                    {!k.revoked && (
                      <Tooltip title="Revoke key">
                        <IconButton size="small" color="error" onClick={() => setRevokeTarget(k)}><RevokeIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Card variant="outlined">
        <CardHeader avatar={<CodeIcon color="primary" />} title="How to send alerts" subheader="Alert intake contract for SIEMs, EDRs and scripts" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" gutterBottom>Endpoint</Typography>
              <CodeBlock>{`POST ${INTAKE_URL}\nX-API-Key: <your key>\nContent-Type: application/json`}</CodeBlock>
              <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Body</Typography>
              <CodeBlock>{`{
  "alerts": [
    {
      "title":         string,
      "description":   string,
      "severity":      "Low" | "Medium" | "High" | "Critical",
      "category":      string,   // e.g. Endpoint, Network
      "source":        string,   // e.g. SIEM, EDR, Firewall
      "sourceId":      string,   // your unique alert id
      "detectionTime": ISO 8601 timestamp,
      "host":          { "hostname": string, "ip": string },
      "user":          string,
      "indicators":    [{ "type": string, "value": string }],
      "tags":          [string],
      "raw":           string | object
    }
  ]
}`}</CodeBlock>
              <Box component="ul" sx={{ pl: 2.5, mt: 2, mb: 0, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">Send up to <strong>100 alerts per call</strong>.</Typography>
                <Typography component="li" variant="body2">Alerts whose <code>sourceId</code> already exists are <strong>skipped as duplicates</strong> (the response reports them with status <code>duplicate</code>).</Typography>
                <Typography component="li" variant="body2">Only one of <code>title</code>, <code>description</code> or <code>raw</code> is required; when only <code>raw</code> is sent the AI parser builds the detection.</Typography>
                <Typography component="li" variant="body2">Critical alerts notify managers and any configured Slack / Teams webhooks.</Typography>
                <Typography component="li" variant="body2">Verify a key with <code>GET {PING_URL}</code> using the same <code>X-API-Key</code> header; it returns <code>{'{ ok: true, company, apiKey }'}</code>.</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">curl example</Typography>
                <Button size="small" startIcon={curlCopied ? <CheckIcon /> : <CopyIcon />} onClick={() => copyCurl(curlExample(examplePrefix))}>
                  {curlCopied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
              <CodeBlock>{curlExample(examplePrefix)}</CodeBlock>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Replace <code>{examplePrefix}...</code> with the full key shown when it was created. Keys always start with <code>exc_</code>.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Test the key</Typography>
              <CodeBlock>{`curl -H "X-API-Key: ${examplePrefix}..." ${PING_URL}`}</CodeBlock>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={creating ? undefined : closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>{createdKey ? 'API key created' : 'Create API key'}</DialogTitle>
        <DialogContent>
          {createdKey ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Copy this key now</AlertTitle>
                This is the only time the full key will be shown. Store it in your SIEM or secret manager; if you lose it, revoke it and create a new one.
              </Alert>
              <TextField
                fullWidth
                label={`Key for "${createdKey.apiKey?.name || ''}"`}
                value={createdKey.key}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={keyCopied ? 'Copied' : 'Copy to clipboard'}>
                        <IconButton onClick={() => copyKey(createdKey.key)} edge="end">
                          {keyCopied ? <CheckIcon color="success" /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                onFocus={(e) => e.target.select()}
              />
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Give the key a name that identifies the system that will use it (for example "Splunk production").
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                label="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                inputProps={{ maxLength: 100 }}
                disabled={creating}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {createdKey ? (
            <Button variant="contained" onClick={closeCreate}>Done</Button>
          ) : (
            <>
              <Button onClick={closeCreate} disabled={creating}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate} disabled={creating || !newName.trim()} startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />}>
                Create
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog open={!!revokeTarget} onClose={revoking ? undefined : () => setRevokeTarget(null)}>
        <DialogTitle>Revoke API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoking <strong>{revokeTarget?.name}</strong> ({revokeTarget?.prefix}…) immediately stops any system using it from sending alerts. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRevoke} disabled={revoking} startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : <RevokeIcon />}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeys;
